import React, { useState, useEffect, useRef } from 'react';
import '../styles/AdminLogs.css';

interface Log {
  id: number;
  log_level: string;
  log_type: string;
  message: string;
  metadata: any;
  user_id: string | null;
  ip_address: string | null;
  endpoint: string | null;
  method: string | null;
  status_code: number | null;
  duration_ms: number | null;
  error_stack: string | null;
  created_at: string;
  archived_at?: string;
  archive_date?: string;
}

interface LogStats {
  total_logs: number;
  error_count: number;
  critical_count: number;
  warn_count: number;
  log_types_count: number;
  unique_users: number;
  oldest_log: string;
  newest_log: string;
}

const AdminLogs: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [logTypes, setLogTypes] = useState<string[]>([]);
  const [logLevels, setLogLevels] = useState<string[]>([]);

  // Filters
  const [filters, setFilters] = useState({
    level: '',
    type: '',
    archived: false,
    limit: 100,
    startDate: '',
    endDate: ''
  });

  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const autoRefreshInterval = useRef<number | null>(null);

  // Fetch available log types and levels
  useEffect(() => {
    fetchLogTypes();
  }, []);

  // Fetch logs when filters change
  useEffect(() => {
    fetchLogs();
  }, [filters]);

  // Auto-refresh logs every 5 seconds if enabled
  useEffect(() => {
    if (autoRefresh && !filters.archived) {
      autoRefreshInterval.current = window.setInterval(() => {
        fetchLogs(true);
      }, 5000);
    } else if (autoRefreshInterval.current) {
      clearInterval(autoRefreshInterval.current);
      autoRefreshInterval.current = null;
    }

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [autoRefresh, filters.archived]);

  // Fetch logs from API
  const fetchLogs = async (silent = false) => {
    if (!silent) setLoading(true);

    try {
      const params = new URLSearchParams();
      if (filters.level) params.append('level', filters.level);
      if (filters.type) params.append('type', filters.type);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      params.append('archived', filters.archived.toString());
      params.append('limit', filters.limit.toString());

      const response = await fetch(`/api/admin/logs?${params.toString()}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch logs');
      }

      const data = await response.json();
      setLogs(data.logs);

      // Fetch stats on first load or manual refresh
      if (!silent) {
        fetchStats();
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Fetch log statistics
  const fetchStats = async () => {
    try {
      const response = await fetch('/api/admin/logs/stats', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch log stats');
      }

      const data = await response.json();
      setStats(data.stats);
    } catch (error) {
      console.error('Error fetching log stats:', error);
    }
  };

  // Fetch available log types and levels
  const fetchLogTypes = async () => {
    try {
      const response = await fetch('/api/admin/logs/types', {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch log types');
      }

      const data = await response.json();
      setLogTypes(data.types);
      setLogLevels(data.levels);
    } catch (error) {
      console.error('Error fetching log types:', error);
    }
  };

  // Manual archive trigger
  const handleArchive = async () => {
    if (!confirm('Archive all logs from previous days? This cannot be undone.')) {
      return;
    }

    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const response = await fetch('/api/admin/logs/archive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ beforeDate: today.toISOString() })
      });

      if (!response.ok) {
        throw new Error('Failed to archive logs');
      }

      const data = await response.json();
      alert(`Successfully archived ${data.archivedCount} logs`);
      fetchLogs();
    } catch (error) {
      console.error('Error archiving logs:', error);
      alert('Failed to archive logs');
    }
  };

  // Get emoji for log level
  const getLevelEmoji = (level: string) => {
    const emojis: { [key: string]: string } = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      critical: '🚨'
    };
    return emojis[level] || 'ℹ️';
  };

  // Get color for log level
  const getLevelColor = (level: string) => {
    const colors: { [key: string]: string } = {
      debug: '#9E9E9E',
      info: '#2196F3',
      warn: '#FF9800',
      error: '#F44336',
      critical: '#D50000'
    };
    return colors[level] || '#757575';
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('lt-LT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Render log details modal
  const renderLogDetails = () => {
    if (!selectedLog) return null;

    return (
      <div className="log-modal-overlay" onClick={() => setSelectedLog(null)}>
        <div className="log-modal" onClick={(e) => e.stopPropagation()}>
          <div className="log-modal-header">
            <h2>Log Details #{selectedLog.id}</h2>
            <button onClick={() => setSelectedLog(null)}>✕</button>
          </div>

          <div className="log-modal-body">
            <div className="log-detail-section">
              <div className="log-detail-item">
                <span className="log-detail-label">Level:</span>
                <span
                  className="log-level-badge"
                  style={{ backgroundColor: getLevelColor(selectedLog.log_level) }}
                >
                  {getLevelEmoji(selectedLog.log_level)} {selectedLog.log_level.toUpperCase()}
                </span>
              </div>

              <div className="log-detail-item">
                <span className="log-detail-label">Type:</span>
                <span className="log-type-badge">{selectedLog.log_type}</span>
              </div>

              <div className="log-detail-item">
                <span className="log-detail-label">Timestamp:</span>
                <span>{formatDate(selectedLog.created_at)}</span>
              </div>

              {selectedLog.user_id && (
                <div className="log-detail-item">
                  <span className="log-detail-label">User ID:</span>
                  <span className="log-user-id">{selectedLog.user_id}</span>
                </div>
              )}

              {selectedLog.endpoint && (
                <div className="log-detail-item">
                  <span className="log-detail-label">Endpoint:</span>
                  <span className="log-endpoint">
                    {selectedLog.method} {selectedLog.endpoint}
                  </span>
                </div>
              )}

              {selectedLog.status_code && (
                <div className="log-detail-item">
                  <span className="log-detail-label">Status Code:</span>
                  <span className={`status-code status-${Math.floor(selectedLog.status_code / 100)}xx`}>
                    {selectedLog.status_code}
                  </span>
                </div>
              )}

              {selectedLog.duration_ms && (
                <div className="log-detail-item">
                  <span className="log-detail-label">Duration:</span>
                  <span>{selectedLog.duration_ms}ms</span>
                </div>
              )}

              {selectedLog.ip_address && (
                <div className="log-detail-item">
                  <span className="log-detail-label">IP Address:</span>
                  <span>{selectedLog.ip_address}</span>
                </div>
              )}
            </div>

            <div className="log-detail-section">
              <h3>Message</h3>
              <pre className="log-message">{selectedLog.message}</pre>
            </div>

            {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
              <div className="log-detail-section">
                <h3>Metadata</h3>
                <pre className="log-metadata">{JSON.stringify(selectedLog.metadata, null, 2)}</pre>
              </div>
            )}

            {selectedLog.error_stack && (
              <div className="log-detail-section">
                <h3>Error Stack</h3>
                <pre className="log-error-stack">{selectedLog.error_stack}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-logs-container">
      <div className="logs-header">
        <h1>📋 Application Logs</h1>
        <div className="logs-actions">
          <label className="auto-refresh-toggle">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              disabled={filters.archived}
            />
            Auto-refresh {!filters.archived && autoRefresh && '(every 5s)'}
          </label>
          <button onClick={() => fetchLogs()} disabled={loading} className="refresh-btn">
            🔄 Refresh
          </button>
          <button onClick={handleArchive} className="archive-btn">
            🗄️ Archive Old Logs
          </button>
        </div>
      </div>

      {/* Statistics */}
      {stats && (
        <div className="logs-stats">
          <div className="stat-card">
            <div className="stat-value">{stats.total_logs.toLocaleString()}</div>
            <div className="stat-label">Total Logs</div>
          </div>
          <div className="stat-card error">
            <div className="stat-value">{stats.error_count.toLocaleString()}</div>
            <div className="stat-label">Errors</div>
          </div>
          <div className="stat-card critical">
            <div className="stat-value">{stats.critical_count.toLocaleString()}</div>
            <div className="stat-label">Critical</div>
          </div>
          <div className="stat-card warn">
            <div className="stat-value">{stats.warn_count.toLocaleString()}</div>
            <div className="stat-label">Warnings</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.log_types_count}</div>
            <div className="stat-label">Log Types</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.unique_users.toLocaleString()}</div>
            <div className="stat-label">Unique Users</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="logs-filters">
        <div className="filter-group">
          <label>Log Level:</label>
          <select
            value={filters.level}
            onChange={(e) => setFilters({ ...filters, level: e.target.value })}
          >
            <option value="">All Levels</option>
            {logLevels.map(level => (
              <option key={level} value={level}>{level.toUpperCase()}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Log Type:</label>
          <select
            value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}
          >
            <option value="">All Types</option>
            {logTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Start Date:</label>
          <input
            type="datetime-local"
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>End Date:</label>
          <input
            type="datetime-local"
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          />
        </div>

        <div className="filter-group">
          <label>Limit:</label>
          <select
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: parseInt(e.target.value) })}
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="250">250</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
          </select>
        </div>

        <div className="filter-group">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={filters.archived}
              onChange={(e) => setFilters({ ...filters, archived: e.target.checked })}
            />
            Show Archived Logs
          </label>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="logs-loading">Loading logs...</div>
      ) : (
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>Time</th>
                <th>Level</th>
                <th>Type</th>
                <th>Message</th>
                <th>Endpoint</th>
                <th>User</th>
                <th>Duration</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="no-logs">No logs found</td>
                </tr>
              ) : (
                logs.map(log => (
                  <tr key={log.id} className={`log-row log-level-${log.log_level}`}>
                    <td className="log-time">{formatDate(log.created_at)}</td>
                    <td>
                      <span
                        className="log-level-badge"
                        style={{ backgroundColor: getLevelColor(log.log_level) }}
                      >
                        {getLevelEmoji(log.log_level)} {log.log_level}
                      </span>
                    </td>
                    <td>
                      <span className="log-type-badge">{log.log_type}</span>
                    </td>
                    <td className="log-message-cell">
                      {log.message.length > 100
                        ? `${log.message.substring(0, 100)}...`
                        : log.message}
                    </td>
                    <td className="log-endpoint">
                      {log.method && log.endpoint ? (
                        <span className="endpoint-badge">
                          <span className={`http-method method-${log.method?.toLowerCase()}`}>
                            {log.method}
                          </span>
                          {log.endpoint}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="log-user">
                      {log.user_id ? log.user_id.substring(0, 8) : '-'}
                    </td>
                    <td className="log-duration">
                      {log.duration_ms ? `${log.duration_ms}ms` : '-'}
                    </td>
                    <td>
                      <button
                        className="view-details-btn"
                        onClick={() => setSelectedLog(log)}
                      >
                        👁️ Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Log Details Modal */}
      {renderLogDetails()}
    </div>
  );
};

export default AdminLogs;
